import { parsePaymentTerms } from "@/lib/backtest/paymentTermsParser";
import type { VendorSummary, PARInvoiceRow } from "@/lib/par/csvInvoiceParser";
import type { VendorTerms } from "@/types/negotiations";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type CaseReasonKey =
  | "never_negotiated"
  | "stale_negotiation"
  | "annual_spend"
  | "no_discount_captured"
  | "paid_early_uncaptured"
  | "long_net_terms";

export interface CaseReason {
  key: CaseReasonKey;
  label: string;
  /** Human-readable data point backing the reason (e.g. "$120,000 annual spend"). */
  detail: string;
}

export interface NegotiationCase {
  headline: string;
  reasons: CaseReason[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** How many days ago was the given ISO date? */
function daysAgo(isoDate: string): number {
  return (Date.now() - new Date(isoDate).getTime()) / (1000 * 60 * 60 * 24);
}

/** Format a dollar amount compactly (e.g. "$1.2M", "$45K", "$500"). */
function formatDollars(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${Math.round(amount / 1_000)}K`;
  return `$${Math.round(amount)}`;
}

/**
 * Parse the maximum net days from a list of terms strings.
 * Returns null when nothing is parseable.
 */
function maxNetDays(termsUsed: string[]): number | null {
  let max: number | null = null;
  for (const t of termsUsed) {
    const parsed = parsePaymentTerms(t);
    if (parsed?.netDays != null) {
      max = max == null ? parsed.netDays : Math.max(max, parsed.netDays);
    }
    // Also handle plain "Net N" strings (no discount prefix)
    const netOnly = t.match(/\bNET\s*(\d+)/i);
    if (netOnly) {
      const n = parseInt(netOnly[1], 10);
      max = max == null ? n : Math.max(max, n);
    }
  }
  return max;
}

// ---------------------------------------------------------------------------
// Core function
// ---------------------------------------------------------------------------

/**
 * Build a data-backed negotiation case for the given vendor.
 *
 * Each reason is only included when the data actually supports it (not padded
 * with weak signals). Headline is derived from the strongest signal.
 */
export function buildNegotiationCase(
  vendorSummary: VendorSummary,
  rows: PARInvoiceRow[],
  vendorTerms?: VendorTerms | null
): NegotiationCase {
  const reasons: CaseReason[] = [];

  // -------------------------------------------------------------------------
  // 1. Last-negotiated age
  // -------------------------------------------------------------------------
  if (vendorTerms == null) {
    reasons.push({
      key: "never_negotiated",
      label: "Never negotiated early-payment terms",
      detail: "No prior agreement on file — a first conversation can set the baseline.",
    });
  } else if (vendorTerms.agreed_at) {
    const ageDays = daysAgo(vendorTerms.agreed_at);
    const ageYears = ageDays / 365;
    if (ageDays >= 180) {
      // Only flag if >= 6 months old
      const ageLabel =
        ageYears >= 2
          ? `${Math.floor(ageYears)} years ago`
          : ageYears >= 1
            ? "over a year ago"
            : `${Math.round(ageDays / 30)} months ago`;
      reasons.push({
        key: "stale_negotiation",
        label: "Terms haven't been reviewed recently",
        detail: `Last agreement was ${ageLabel} — market conditions may have shifted.`,
      });
    }
  }

  // -------------------------------------------------------------------------
  // 2. Annual spend
  // Only flag if the spend is meaningful; calibrate the label to the actual amount.
  // Floors: >$50K = "Substantial", >$10K = "Moderate", >$0 = plain statement.
  // "Significant" / "High" are reserved for >$50K to avoid misleading operators.
  // -------------------------------------------------------------------------
  const SPEND_FLOOR = 0; // include any spend > 0 but vary wording
  const SIGNIFICANT_SPEND_FLOOR = 50_000;
  const MODERATE_SPEND_FLOOR = 10_000;

  if (vendorSummary.total_invoiced > SPEND_FLOOR) {
    const amt = vendorSummary.total_invoiced;
    let label: string;
    let detail: string;

    if (amt >= SIGNIFICANT_SPEND_FLOOR) {
      label = "Significant spend volume";
      detail = `${formatDollars(amt)} invoiced — even a small discount rate delivers meaningful savings.`;
    } else if (amt >= MODERATE_SPEND_FLOOR) {
      label = "Moderate spend volume";
      detail = `${formatDollars(amt)} invoiced — a negotiated discount would capture recoverable savings.`;
    } else {
      label = "Spend on record";
      detail = `${formatDollars(amt)} invoiced — verify whether early-payment terms make sense at this volume.`;
    }

    reasons.push({ key: "annual_spend", label, detail });
  }

  // -------------------------------------------------------------------------
  // 3. No early-pay discount captured (terms exist but no discount taken)
  // -------------------------------------------------------------------------
  const NEAR_ZERO = 0.01;
  if (
    vendorSummary.best_discount_pct != null &&
    vendorSummary.best_discount_pct > 0 &&
    vendorSummary.total_discount <= NEAR_ZERO
  ) {
    reasons.push({
      key: "no_discount_captured",
      label: "Discount terms available but never used",
      detail: `Up to ${vendorSummary.best_discount_pct.toFixed(2)}% discount on offer — formalising this would capture missed savings.`,
    });
  }

  // -------------------------------------------------------------------------
  // 4. Paid early with no discount captured
  // -------------------------------------------------------------------------
  if (vendorSummary.avg_days_paid_early > 0 && vendorSummary.total_discount <= NEAR_ZERO) {
    const avgDays = Math.round(vendorSummary.avg_days_paid_early);
    reasons.push({
      key: "paid_early_uncaptured",
      label: "Already paying early — without capturing a discount",
      detail: `On average ${avgDays} day${avgDays !== 1 ? "s" : ""} before due date, yet no discount has been captured.`,
    });
  }

  // -------------------------------------------------------------------------
  // 5. Long net terms
  // -------------------------------------------------------------------------
  const vendorRows = rows.filter((r) => r.vendor_name === vendorSummary.vendor_name);
  const allTerms = [...vendorSummary.terms_used, ...vendorRows.map((r) => r.terms).filter(Boolean)];
  const maxNet = maxNetDays(allTerms);
  if (maxNet != null && maxNet >= 45) {
    reasons.push({
      key: "long_net_terms",
      label: "Long payment window creates leverage",
      detail: `Net ${maxNet} terms mean a sizeable discount window — a negotiated early-pay rate benefits both parties.`,
    });
  }

  // -------------------------------------------------------------------------
  // Headline — derive from strongest signal
  // -------------------------------------------------------------------------
  let headline: string;

  const hasNever = reasons.some((r) => r.key === "never_negotiated");
  const hasStale = reasons.some((r) => r.key === "stale_negotiation");
  const hasPaidEarly = reasons.some((r) => r.key === "paid_early_uncaptured");
  const hasNoCapture = reasons.some((r) => r.key === "no_discount_captured");

  if (hasNever) {
    headline = `${vendorSummary.vendor_name} has never been approached for early-payment terms.`;
  } else if (hasStale) {
    headline = `Terms with ${vendorSummary.vendor_name} are overdue for review.`;
  } else if (hasPaidEarly) {
    headline = `${vendorSummary.vendor_name}: paying early without capturing any discount.`;
  } else if (hasNoCapture) {
    headline = `${vendorSummary.vendor_name} offers a discount that has never been used.`;
  } else {
    headline = `${vendorSummary.vendor_name} is a strong candidate for early-payment terms.`;
  }

  return { headline, reasons };
}
